import { useState, useEffect, useCallback } from 'react';
import { type User, SystemRole, isAdmin } from '@peles/shared';
import { useUser, useUpdateUser, useDeactivateUser } from '../../hooks/useUsers';
import { UserAvatar } from '../common/UserAvatar';
import { AuditInfoTooltip } from '../common/AuditInfoTooltip';
import { useDialog } from '../../context/DialogContext';
import { GoogleAddressAutocomplete } from '../common/GoogleAddressAutocomplete';
import { PhoneInput } from '../common/PhoneInput';

interface UserDetailPanelProps {
  userId: string;
  onClose: () => void;
  onUnsavedChange: (dirty: boolean) => void;
  currentUser: User;
}

interface FormState {
  name: string;
  phone: string;
  address: string;
  systemRole: SystemRole;
  isActive: boolean;
}

function userToFormState(user: User): FormState {
  return {
    name: user.name,
    phone: user.phone || '',
    address: user.address || '',
    systemRole: user.systemRole,
    isActive: user.isActive,
  };
}

function isDirty(form: FormState, original: FormState): boolean {
  return (
    form.name !== original.name ||
    form.phone !== original.phone ||
    form.address !== original.address ||
    form.systemRole !== original.systemRole ||
    form.isActive !== original.isActive
  );
}

export function UserDetailPanel({
  userId,
  onClose,
  onUnsavedChange,
  currentUser,
}: UserDetailPanelProps) {
  const { confirm } = useDialog();
  const { data: user, isLoading } = useUser(userId);
  const updateUser = useUpdateUser();
  const deactivateUser = useDeactivateUser();

  const [form, setForm] = useState<FormState>({
    name: '',
    phone: '',
    address: '',
    systemRole: SystemRole.CLIENT,
    isActive: true,
  });
  const [originalForm, setOriginalForm] = useState<FormState>(form);
  const [saved, setSaved] = useState(false);

  const currentUserIsAdmin = isAdmin(currentUser.systemRole);

  // Initialize form when user data loads or userId changes
  useEffect(() => {
    if (user) {
      const state = userToFormState(user);
      setForm(state);
      setOriginalForm(state);
      setSaved(false);
    }
  }, [user]);

  // Track dirty state
  const dirty = isDirty(form, originalForm);
  useEffect(() => {
    onUnsavedChange(dirty);
  }, [dirty, onUnsavedChange]);

  const handleSave = useCallback(() => {
    const updates: Record<string, unknown> = {};
    if (form.name !== originalForm.name) updates.name = form.name;
    if (form.phone !== originalForm.phone) updates.phone = form.phone || null;
    if (form.address !== originalForm.address) updates.address = form.address || null;
    if (currentUserIsAdmin && form.systemRole !== originalForm.systemRole)
      updates.systemRole = form.systemRole;
    if (currentUserIsAdmin && form.isActive !== originalForm.isActive)
      updates.isActive = form.isActive;

    updateUser.mutate(
      { id: userId, updates },
      {
        onSuccess: (updated) => {
          const state = userToFormState(updated);
          setForm(state);
          setOriginalForm(state);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }, [form, originalForm, userId, currentUserIsAdmin, updateUser]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDeactivate = useCallback(async () => {
    if (
      await confirm({
        title: 'Deactivate User',
        message: `Deactivate ${user?.name}? They will no longer be able to log in.`,
        confirmLabel: 'Deactivate',
      })
    ) {
      deactivateUser.mutate(userId);
    }
  }, [userId, user?.name, deactivateUser, confirm]);

  if (isLoading) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">User not found</p>
      </div>
    );
  }

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex items-center gap-3 mb-6">
        <UserAvatar user={user} size="lg" />
        <h2 className="text-lg font-semibold flex-1">User Details</h2>
        {currentUserIsAdmin && <AuditInfoTooltip entityType="User" entityId={userId} />}
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {saved && (
        <div className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
          Changes saved successfully
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="detail-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            id="detail-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <p className="text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded-md">
            {user.email ?? 'No email — reached by phone, cannot sign in'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
          <p className="text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded-md">
            {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
          </p>
        </div>

        <div>
          <label htmlFor="detail-phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <PhoneInput
            id="detail-phone"
            value={form.phone}
            onChange={(phone) => setForm((f) => ({ ...f, phone }))}
            placeholder="Enter phone number"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="detail-address" className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <GoogleAddressAutocomplete
            id="detail-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onAddressSelect={(fields) =>
              setForm((f) => ({ ...f, address: fields.formattedAddress }))
            }
            placeholder="Enter address"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {currentUserIsAdmin && (
          <>
            <div>
              <label htmlFor="detail-role" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                id="detail-role"
                value={form.systemRole}
                onChange={(e) =>
                  setForm((f) => ({ ...f, systemRole: e.target.value as SystemRole }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {Object.values(SystemRole).map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Active</label>
              <button
                type="button"
                role="switch"
                aria-checked={form.isActive}
                onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.isActive ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleSave}
          disabled={!dirty || updateUser.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateUser.isPending ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>

      {currentUserIsAdmin && user.isActive && userId !== currentUser.id && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={handleDeactivate}
            disabled={deactivateUser.isPending}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {deactivateUser.isPending ? 'Deactivating...' : 'Deactivate User'}
          </button>
        </div>
      )}
    </div>
  );
}
